import { withErrorBoundary } from 'app/components/error-boundary';
import { Column, Row } from 'app/design/primitives/box';
import { Page } from 'app/design/primitives/page';
import { Radio } from 'app/design/primitives/radio';
import { Text } from 'app/design/primitives/text';
import { useNavigationOptions } from 'app/hooks/navigation';
import { useOnePreference, useUpsertOnePreference } from 'app/network/preferences';
import type { NavigationScreen } from 'app/types/navigation';
import type { AddToCalendarType } from 'app/types/preferences';
import { AddToCalendar } from 'app/types/preferences';
import { usePageTitle } from 'app/utils/web-helpers';
import * as Calendar from 'expo-calendar/legacy';
import { PermissionStatus } from 'expo';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, RefreshControl } from 'react-native';
import { ModalCalendarSetting } from './modal';

const CalendarSettingScreenComponent: NavigationScreen<'calendar-setting'> = ({ navigation }) => {
  const { t } = useTranslation('calendar-settings');
  const { data: addToCalendarPreference, refetch, isRefetching } = useOnePreference('addToCalendar');
  const upsertAddToCalendarPreference = useUpsertOnePreference('addToCalendar');

  const modalState = useState(false);

  const setPageTitle = usePageTitle('calendar-settings');
  useEffect(() => setPageTitle('title'), [setPageTitle]);

  useNavigationOptions(
    navigation,
    {
      headerTitle: () => (
        <Text variant="bodyXLargeMedium" color="white-label.header.on-header">
          {t(`title`)}
        </Text>
      ),
    },
    false,
  );

  const onAddToCalendarChange = async (to: AddToCalendarType) => {
    // @ts-expect-error to is never type because of array values
    if ([AddToCalendar.always, AddToCalendar.once].includes(to)) {
      const { status } = await Calendar.requestCalendarPermissionsAsync();

      if (status !== PermissionStatus.GRANTED) {
        modalState[1](true);
        return;
      }
    }
    upsertAddToCalendarPreference.mutate(to);
  };

  return (
    <Page
      refreshControl={<RefreshControl onRefresh={() => void refetch()} refreshing={isRefetching} />}
      title={t('title')}
    >
      <Column gap="spacing.general.x4">
        {Object.values(AddToCalendar).map((option) => (
          <Pressable key={option} onPress={() => void onAddToCalendarChange(option)}>
            <Row alignItems="center" flex={1}>
              <Radio
                checked={option === addToCalendarPreference?.value}
                onPress={() => void onAddToCalendarChange(option)}
              />
              <Text fontSize={16} marginStart="spacing.general.x4" variant="bodyBase">
                {t(`addToCalendar.${option}`, { ns: 'my-preferences' })}
              </Text>
            </Row>
          </Pressable>
        ))}
      </Column>

      <ModalCalendarSetting modalState={modalState} />
    </Page>
  );
};

export const CalendarSettingScreen: NavigationScreen<'calendar-setting'> =
  withErrorBoundary(CalendarSettingScreenComponent);
